/**
 * Chinese localisation overlay for the ADB / Control command catalog.
 *
 * The backend `CommandSpec` keeps a single English source of truth
 * (`displayName`, `description`, JSON schema field labels). On the web side we
 * apply this map to render Chinese titles, descriptions, field labels and
 * input presets without forking the spec.
 *
 * Field `presets` are consumed by `JsonSchemaForm`, which attaches a
 * `<datalist>` to text inputs so users can pick frequent values quickly.
 */

export interface FieldLocale {
  /** Field display label (replaces JSON schema title). */
  label?: string;
  /** Helper text under the input. */
  hint?: string;
  /** Placeholder shown in the input. */
  placeholder?: string;
  /** Suggested values surfaced via <datalist>. */
  presets?: string[];
}

export interface CmdLocale {
  title?: string;
  desc?: string;
  fields?: Record<string, FieldLocale>;
}

export const NAMESPACE_CN: Record<string, string> = {
  device: '设备',
  fs: '文件系统',
  app: '应用',
  screen: '截屏录像',
  input: '输入',
  net: '网络',
  perf: '性能',
  proc: '进程',
  clipboard: '剪贴板',
  bugreport: 'Bug 报告',
  lifecycle: '生命周期',
  system: '系统',
  logcat: '日志',
  prop: '属性',
  shell: 'Shell',
};

const PATH_PRESETS = [
  '/sdcard/',
  '/sdcard/Download/',
  '/sdcard/DCIM/',
  '/sdcard/Pictures/',
  '/sdcard/Movies/',
  '/data/local/tmp/',
];

const DEVICE_FILE_PRESETS = [
  '/sdcard/Download/sample.txt',
  '/sdcard/Pictures/Screenshots/screenshot.png',
  '/sdcard/DCIM/Camera/video.mp4',
];

const SERIAL_PRESETS_HINT = '可在 device picker 中切换设备，无需手动填写';

const PACKAGE_PRESETS = [
  'com.android.settings',
  'com.android.chrome',
  'com.android.systemui',
];

export const CMD_I18N: Record<string, CmdLocale> = {
  /* ------------------------- system / shell ------------------------- */
  'system.ping': { title: '心跳检查', desc: '探活：返回毫秒时间戳' },
  'system.now': { title: '获取时间', desc: '读取设备墙钟与时区信息' },
  'system.reboot': {
    title: '重启设备',
    desc: '执行 adb reboot；可选进入 recovery / bootloader',
    fields: { mode: { label: '模式', hint: '默认为 normal 重启' } },
  },
  'system.catalog': { title: '命令目录', desc: '列出当前进程注册的所有命令' },
  'shell.exec': {
    title: '执行 Shell',
    desc: '在设备上运行任意 shell 命令（高危，谨慎使用）',
    fields: {
      cmd: {
        label: '命令',
        placeholder: 'getprop ro.build.version.release',
        presets: ['getprop', 'ls /sdcard', 'pm list packages -3', 'dumpsys battery', 'ps -A'],
      },
      timeoutMs: { label: '超时 (毫秒)', hint: '默认 10000' },
    },
  },

  /* ----------------------------- device ----------------------------- */
  'device.info': { title: '设备信息', desc: '品牌、型号、系统、指纹等总览' },
  'device.battery': { title: '电池状态', desc: '电量、温度、健康度、充电类型' },
  'device.locale': { title: '语言区域', desc: '当前 Locale、时区与键盘' },
  'device.screen': { title: '屏幕参数', desc: '分辨率、密度、方向' },
  'device.uptime': { title: '运行时长', desc: '开机至今的秒数与可读字符串' },
  'device.powerState': {
    title: '电源 / 屏幕状态',
    desc: '解析 dumpsys power，告诉你手机是否亮屏、是否可交互。浮窗投屏用它判断"是不是锁屏了"。',
  },

  /* ------------------------------- fs ------------------------------- */
  'fs.list': {
    title: '列出目录',
    desc: '查看设备上某个目录的文件列表',
    fields: {
      path: { label: '路径', presets: PATH_PRESETS, placeholder: '/sdcard/' },
    },
  },
  'fs.read': {
    title: '读取文件',
    desc: '读取小文本文件（仅前 N KB）',
    fields: {
      path: { label: '路径', presets: DEVICE_FILE_PRESETS },
      maxBytes: { label: '最大字节数', hint: '建议 ≤ 64KB' },
    },
  },
  'fs.exists': { title: '路径存在?', fields: { path: { label: '路径', presets: PATH_PRESETS } } },
  'fs.pull': {
    title: '从设备拉取',
    desc: '把设备上的文件拉到 PC 本地',
    fields: {
      remote: { label: '设备路径', presets: DEVICE_FILE_PRESETS },
      local: { label: '服务器保存路径', placeholder: '可留空，自动写入 devices/<serial>/' },
    },
  },
  'fs.push': {
    title: '推送到设备',
    desc: '把 PC 上的文件推送到手机',
    fields: {
      local: { label: '本机文件', hint: '点击选择电脑上的任意文件，自动上传到服务器后推送' },
      remote: { label: '设备目标路径', placeholder: '/sdcard/Download/...' },
    },
  },
  'fs.mkdir': {
    title: '创建目录',
    fields: {
      path: { label: '路径', placeholder: '/sdcard/Download/new-folder' },
      parents: { label: '递归创建父目录', hint: '相当于 mkdir -p' },
    },
  },
  'fs.remove': {
    title: '删除文件 / 目录',
    desc: '危险：rm -rf；删除前请二次确认',
    fields: {
      path: { label: '路径' },
      recursive: { label: '递归删除' },
    },
  },
  'fs.move': {
    title: '移动 / 重命名',
    fields: {
      from: { label: '源路径' },
      to: { label: '目标路径' },
    },
  },
  'fs.stat': {
    title: '查看属性',
    desc: '权限、大小、mtime',
    fields: { path: { label: '路径', presets: PATH_PRESETS } },
  },
  'fs.diskUsage': {
    title: '磁盘占用',
    desc: '调用 df 查询挂载点占用',
    fields: {
      path: { label: '挂载点', placeholder: '/data', presets: ['/data', '/sdcard', '/cache', '/system'] },
    },
  },

  /* ------------------------------ app ------------------------------ */
  'app.list': {
    title: '应用列表',
    desc: '列出第三方 / 系统包',
    fields: {
      // Field names MUST match the backend schema in src/adb-toolbox/builtins/app.ts;
      // a drift here makes the Commands page submit unknown properties and the
      // backend's strict validator throws `INVALID_ARGS: $ has unknown property`.
      onlyThirdParty: { label: '仅第三方应用', hint: '相当于 pm list packages -3' },
      onlySystem: { label: '仅系统应用', hint: '相当于 pm list packages -s' },
      filter: { label: '名称包含', placeholder: 'com.example', hint: '空字符串表示不过滤' },
    },
  },
  'app.list.detailed': {
    title: '应用列表（详细）',
    desc: '一次性返回包名 + APK 路径 + versionCode + 系统/三方标记，应用管理页用',
    fields: {
      onlyThirdParty: { label: '仅第三方应用' },
      onlySystem: { label: '仅系统应用' },
      filter: { label: '名称包含', placeholder: 'com.example' },
    },
  },
  'app.icon': {
    title: '读取应用图标',
    desc: '拉取 APK 并解析图标 / 应用名（带缓存）。同一 versionCode 命中后秒返。',
    fields: {
      package: { label: '包名', presets: PACKAGE_PRESETS },
      apkPath: { label: '设备上的 APK 路径（可选）', hint: '留空时自动 pm path 解析' },
      versionCode: { label: 'versionCode（可选）', hint: '用于缓存 key；留空时不带版本' },
    },
  },
  'app.pullApk': {
    title: '导出 APK 到电脑',
    desc: '从设备 pull 安装好的 APK 到 hub 的 devices 目录，可在文件页面下载到本机',
    fields: {
      package: { label: '包名', presets: PACKAGE_PRESETS },
      apkPath: { label: '设备上的 APK 路径（可选）' },
      versionCode: { label: 'versionCode（可选）', hint: '仅用于文件命名' },
    },
  },
  'app.info': {
    title: '应用详情',
    fields: { package: { label: '包名', presets: PACKAGE_PRESETS, placeholder: 'com.example' } },
  },
  'app.foreground': { title: '前台应用', desc: '当前前台 Activity / Package' },
  'app.start': {
    title: '启动 App',
    desc: '通过 Activity 名启动应用',
    fields: {
      package: { label: '包名', presets: PACKAGE_PRESETS },
      activity: { label: '组件名（可选）', placeholder: '.MainActivity', hint: '留空走 LAUNCHER intent' },
      userId: {
        label: '用户 ID（可选）',
        placeholder: '0',
        hint: '多用户 / 工作资料设备：主用户 0，工作资料常见 10 / 11，ColorOS 应用分身 999',
      },
    },
  },
  'app.stop': {
    title: '强制停止',
    desc: 'am force-stop',
    fields: {
      package: { label: '包名', presets: PACKAGE_PRESETS },
      userId: { label: '用户 ID（可选）', placeholder: '0' },
    },
  },
  'app.clear': {
    title: '清除数据',
    desc: 'pm clear（不可恢复）',
    fields: {
      package: { label: '包名', presets: PACKAGE_PRESETS },
      userId: { label: '用户 ID（可选）', placeholder: '0', hint: '不传则自动尝试 default → --user 0 兜底' },
    },
  },
  'app.enable': {
    title: '启用应用',
    desc: 'pm enable，把先前被 disable-user 的包重新放回启动器',
    fields: {
      package: { label: '包名', presets: PACKAGE_PRESETS },
      userId: { label: '用户 ID（可选）', placeholder: '0' },
    },
  },
  'app.disable': {
    title: '停用应用',
    desc: 'pm disable-user，从启动器隐藏但保留数据（系统 bloatware 治理首选）',
    fields: {
      package: { label: '包名', presets: PACKAGE_PRESETS },
      userId: { label: '用户 ID（可选）', placeholder: '0', hint: '默认 0；该命令必须带 --user' },
    },
  },
  'app.install': {
    title: '安装 APK',
    fields: {
      apkPath: { label: '本机 APK 文件', hint: '点击选择电脑上的 .apk，自动上传后安装到当前设备' },
      // The backend schema uses `reinstall` / `downgrade` (mapping to `adb install -r` / `-d`),
      // not `replace` / `grantRuntimePermissions`. Earlier wording surfaced INVALID_ARGS at
      // submit time because the SPA was sending fields the schema doesn't know about.
      reinstall: { label: '覆盖安装', hint: '相当于 adb install -r' },
      downgrade: { label: '允许降级', hint: '相当于 adb install -d；versionCode 比设备低时也可装' },
    },
  },
  'app.startIntent': {
    title: '发送 Intent',
    desc: '通过 am start 自定义 Intent 启动',
    fields: {
      action: { label: 'action', placeholder: 'android.intent.action.VIEW' },
      data: { label: 'data', placeholder: 'https://example.com' },
      package: { label: 'package', presets: PACKAGE_PRESETS },
      component: { label: 'component', placeholder: 'com.example/.Main' },
      category: { label: 'category', placeholder: 'android.intent.category.DEFAULT' },
      flags: { label: 'flags' },
      extras: { label: 'extras', hint: '键值对，每行一个 key=value' },
    },
  },
  'app.dumpActivity': { title: '当前 Activity 信息', desc: '前台进程 / Activity / PID' },
  'app.uninstall': {
    title: '卸载应用',
    desc: 'pm uninstall（不可恢复）',
    fields: {
      package: { label: '包名', presets: PACKAGE_PRESETS },
      keepData: { label: '保留数据' },
      userId: { label: '用户 ID（可选）', placeholder: '0', hint: '指定后走 pm uninstall --user N' },
    },
  },
  'app.storage': {
    title: '应用存储占用',
    desc: 'APK / 数据 / 缓存大小；data 和 cache 在多数 ROM 上需 root，访问失败返回 null',
    fields: { package: { label: '包名', presets: PACKAGE_PRESETS } },
  },
  'app.activities': {
    title: 'Activity 列表',
    desc: '解析 dumpsys package 的 Activity Resolver Table，列出带 intent-filter 的 Activity',
    fields: {
      package: { label: '包名', presets: PACKAGE_PRESETS },
      exportedOnly: { label: '只看 exported', hint: '只显示对外可访问的 Activity' },
    },
  },
  'app.permissions': {
    title: '权限列表',
    desc: '解析 dumpsys package 的 requested / install / runtime 三段权限',
    fields: { package: { label: '包名', presets: PACKAGE_PRESETS } },
  },
  'app.grant': {
    title: '授予权限',
    desc: 'pm grant；仅运行时(dangerous)权限可调',
    fields: {
      package: { label: '包名', presets: PACKAGE_PRESETS },
      permission: {
        label: '权限名',
        placeholder: 'android.permission.CAMERA',
        hint: '完整 permission 全称',
      },
      userId: { label: '用户 ID（可选）', placeholder: '0' },
    },
  },
  'app.revoke': {
    title: '撤销权限',
    desc: 'pm revoke；可能触发应用自重启',
    fields: {
      package: { label: '包名', presets: PACKAGE_PRESETS },
      permission: { label: '权限名', placeholder: 'android.permission.CAMERA' },
      userId: { label: '用户 ID（可选）', placeholder: '0' },
    },
  },
  'app.pullApks': {
    title: '导出 APK（含 split）',
    desc: '把 base.apk 与所有 split_config.*.apk 一起导出到 hub，可用于 install-multiple 重装',
    fields: {
      package: { label: '包名', presets: PACKAGE_PRESETS },
      versionCode: { label: 'versionCode（可选）', hint: '仅用于目录命名' },
    },
  },
  'app.installMultiple': {
    title: '安装 APK 套装',
    desc: 'adb install-multiple，把一组 base + split APK 作为同一个 split bundle 安装',
    fields: {
      apkPaths: {
        label: '本机 APK 文件列表',
        hint: '一组本地路径（base + split），通常来自 app.pullApks 的产物',
      },
      reinstall: { label: '覆盖安装', hint: '相当于 install-multiple -r' },
      downgrade: { label: '允许降级', hint: '相当于 install-multiple -d' },
    },
  },

  /* ----------------------------- screen ---------------------------- */
  'screen.capture': {
    title: '截图',
    desc: '截屏并保存到本机 devices/<serial>/screenshots/',
  },

  /* ----------------------------- input ----------------------------- */
  'input.tap': {
    title: '点击',
    fields: {
      x: { label: 'X (px)' },
      y: { label: 'Y (px)' },
    },
  },
  'input.swipe': {
    title: '滑动',
    fields: {
      x1: { label: '起点 X' },
      y1: { label: '起点 Y' },
      x2: { label: '终点 X' },
      y2: { label: '终点 Y' },
      durationMs: { label: '时长 (ms)' },
    },
  },
  'input.text': {
    title: '输入文本',
    desc: '通过 input text 注入；不支持中文输入',
    fields: { text: { label: '文本', placeholder: 'hello' } },
  },
  'input.key': {
    title: '按键',
    fields: {
      key: {
        label: '按键',
        presets: ['HOME', 'BACK', 'MENU', 'POWER', 'VOLUME_UP', 'VOLUME_DOWN', 'WAKEUP', 'SLEEP', 'ENTER'],
      },
    },
  },

  /* ------------------------------ net ------------------------------ */
  'net.ip': { title: '网络地址', desc: 'IPv4 / IPv6 / WiFi 网关' },
  'net.state': { title: '联网状态', desc: 'WiFi / 移动网络 / 是否联网' },
  'net.wifi.status': {
    title: 'WiFi 状态',
    desc: '解析 dumpsys wifi 拿到 SSID / RSSI / 链路速率 / 频段',
  },
  'net.wifi.enable': { title: '开启 WiFi', desc: 'svc wifi enable（无 root 也能用）' },
  'net.wifi.disable': { title: '关闭 WiFi', desc: 'svc wifi disable' },
  'net.mobile.enable': { title: '开启移动数据', desc: 'svc data enable' },
  'net.mobile.disable': { title: '关闭移动数据', desc: 'svc data disable' },
  'net.airplane.get': { title: '飞行模式状态', desc: '读 settings global airplane_mode_on' },
  'net.airplane.set': {
    title: '设置飞行模式',
    desc: 'settings put + AIRPLANE_MODE 广播；部分机型只能改 settings 标志位',
    fields: { on: { label: '开启' } },
  },
  'net.proxy.get': { title: '查看 HTTP 代理', desc: '读 settings global http_proxy' },
  'net.proxy.set': {
    title: '设置 HTTP 代理',
    desc: '抓包常用：把流量导向 Charles / mitm / Whistle',
    fields: {
      host: { label: 'Host', placeholder: '192.168.1.10', presets: ['127.0.0.1'] },
      port: { label: 'Port', placeholder: '8888', presets: ['8888', '8080', '8899'] },
    },
  },
  'net.proxy.clear': { title: '清除 HTTP 代理', desc: 'settings put http_proxy :0' },
  'net.privateDns.get': { title: '查看 Private DNS', desc: 'Android 9+ 私有 DNS 模式' },
  'net.privateDns.set': {
    title: '设置 Private DNS',
    desc: 'Android 9+：off / opportunistic / hostname',
    fields: {
      mode: { label: '模式' },
      hostname: { label: 'Hostname', placeholder: 'dns.alidns.com' },
    },
  },
  'net.cert.push': {
    title: '推送 CA 证书',
    desc: '把本地 .crt/.pem 推到 /sdcard/Download，并跳到系统证书安装界面',
    fields: {
      localPath: { label: '本机证书文件', hint: '选择电脑上的 .pem / .crt / .cer 文件，自动上传后推送到设备' },
      openSettings: { label: '推送后打开系统设置' },
    },
  },

  /* ------------------------------ perf ----------------------------- */
  'perf.meminfo': {
    title: '内存信息',
    fields: { package: { label: '包名（可选）', presets: PACKAGE_PRESETS } },
  },
  'perf.cpuinfo': { title: 'CPU 信息', desc: 'CPU 核数 / 频率 / 当前负载' },
  'perf.gfxinfo': {
    title: '渲染统计',
    desc: 'gfxinfo framestats',
    fields: {
      package: { label: '包名', presets: PACKAGE_PRESETS },
      reset: { label: '清零', hint: '采集前先 reset' },
    },
  },
  'perf.battery': { title: '电池开销', desc: 'dumpsys batterystats 摘要' },

  /* ------------------------------ proc ----------------------------- */
  'proc.list': {
    title: '进程列表',
    fields: { filter: { label: '过滤词', placeholder: 'com.example' } },
  },
  'proc.kill': {
    title: '杀掉进程',
    desc: '危险：kill -9',
    fields: {
      pid: { label: 'PID' },
      signal: { label: '信号', hint: '默认 SIGKILL；可填 SIGTERM / SIGINT 等' },
    },
  },

  /* --------------------------- clipboard -------------------------- */
  'clipboard.get': { title: '读取剪贴板', desc: '需要 IME 支持' },
  'clipboard.set': { title: '写入剪贴板', fields: { text: { label: '文本' } } },

  /* ---------------------------- bugreport ------------------------- */
  'bugreport.collect': { title: '收集 bugreport', desc: '调用 bugreportz，耗时较长' },

  /* ---------------------------- lifecycle ------------------------- */
  'lifecycle.lock': { title: '熄屏锁定', desc: '相当于按下 Power' },
  'lifecycle.unlock': { title: '点亮 + 上滑', desc: 'wakeup + 滑动解锁' },
  'lifecycle.recents': { title: '打开多任务', desc: '触发 KEYCODE_APP_SWITCH' },

  /* ---------------------------- logcat --------------------------- */
  'logcat.clear': { title: '清空 logcat 缓冲', desc: '相当于 logcat -c' },
  'logcat.tail': {
    title: '采集一段日志',
    desc: '采集指定时长的 logcat，用于离线分析',
    fields: {
      durationMs: { label: '时长 (ms)', hint: '建议 1000~30000', presets: ['1000', '5000', '10000', '30000'] },
      tags: { label: '标签过滤', placeholder: 'AndroidRuntime,System.err' },
      filterSpec: { label: '过滤规则', hint: '例 *:E AndroidRuntime:V' },
      outPath: { label: '保存路径（可选）', hint: '留空时自动写入 devices/<serial>/logcat/' },
    },
  },

  /* ---------------------------- prop ----------------------------- */
  'prop.get': {
    title: '读取系统属性',
    fields: {
      key: {
        label: 'key',
        presets: ['ro.build.version.release', 'ro.product.model', 'ro.product.brand', 'ro.serialno', 'persist.sys.locale'],
      },
    },
  },
  'prop.set': {
    title: '写入系统属性',
    desc: '危险：仅 system 属性可写',
    fields: {
      key: { label: 'key' },
      value: { label: 'value' },
    },
  },
};

export function localizeCommand(name: string): CmdLocale | undefined {
  return CMD_I18N[name];
}

export function localizeNamespace(ns: string): string {
  return NAMESPACE_CN[ns] ?? ns;
}

export { SERIAL_PRESETS_HINT };
